/**
 * Voice Error Handler
 * Centralized error handling for voice services with user-friendly messages
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

class VoiceErrorHandler {
  constructor() {
    this.errorCounts = new Map();
    this.maxRetries = 3;
    this.errorCategories = {
      PERMISSION: 'permission',
      NETWORK: 'network',
      HARDWARE: 'hardware',
      BROWSER: 'browser',
      USER: 'user',
      SYSTEM: 'system'
    };
  }

  /**
   * Parse speech recognition error event
   * @param {SpeechRecognitionErrorEvent} event - Error event
   * @returns {Object} - Parsed error object
   */
  parseRecognitionError(event) {
    const errorType = event.error;
    const errorMessage = event.message || '';
    
    let category = this.errorCategories.SYSTEM;
    let userMessage = '';
    let isRetryable = false;
    let suggestions = [];

    switch (errorType) {
      case 'no-speech':
        category = this.errorCategories.USER;
        userMessage = 'No speech was detected. Please try speaking louder and more clearly.';
        isRetryable = true;
        suggestions = [
          'Make sure your microphone is not muted',
          'Speak closer to your microphone',
          'Try speaking more clearly'
        ];
        break;

      case 'aborted':
        category = this.errorCategories.USER;
        userMessage = 'Voice recording was cancelled.';
        isRetryable = true;
        break;

      case 'audio-capture':
        category = this.errorCategories.HARDWARE;
        userMessage = 'Could not access your microphone. Please check your microphone connection.';
        isRetryable = true;
        suggestions = [
          'Check that your microphone is connected',
          'Make sure no other app is using your microphone',
          'Try refreshing the page'
        ];
        break;

      case 'network':
        category = this.errorCategories.NETWORK;
        userMessage = 'Network error occurred. Please check your internet connection.';
        isRetryable = true;
        suggestions = [
          'Check your internet connection',
          'Try again in a few moments'
        ];
        break;

      case 'not-allowed':
        category = this.errorCategories.PERMISSION;
        userMessage = 'Microphone access was denied. Please allow microphone access in your browser settings.';
        isRetryable = false;
        suggestions = [
          'Click the microphone icon in your browser\'s address bar',
          'Go to browser settings and allow microphone access for this site',
          'Refresh the page and try again'
        ];
        break;

      case 'service-not-allowed':
        category = this.errorCategories.PERMISSION;
        userMessage = 'Speech recognition service is not allowed. Please check your browser settings.';
        isRetryable = false;
        suggestions = [
          'Check your browser\'s privacy settings',
          'Make sure speech recognition is enabled'
        ];
        break;

      case 'bad-grammar':
        category = this.errorCategories.BROWSER;
        userMessage = 'Speech recognition configuration error. Please try again.';
        isRetryable = true;
        break;

      case 'language-not-supported':
        category = this.errorCategories.BROWSER;
        userMessage = 'The selected language is not supported for speech recognition.';
        isRetryable = false;
        suggestions = [
          'Try switching to English (US)',
          'Check if your browser supports your language'
        ];
        break;

      default:
        category = this.errorCategories.SYSTEM;
        userMessage = `Speech recognition error: ${errorType}. Please try again.`;
        isRetryable = true;
        break;
    }

    return {
      type: errorType,
      category,
      message: errorMessage,
      userMessage,
      isRetryable,
      suggestions,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Parse general error
   * @param {Error} error - Error object
   * @returns {Object} - Parsed error object
   */
  parseError(error) {
    const message = error.message || error.toString();
    let category = this.errorCategories.SYSTEM;
    let userMessage = 'An unexpected error occurred. Please try again.';
    let isRetryable = true;
    let suggestions = [];

    // Permission errors
    if (message.includes('permission') || message.includes('denied') || message.includes('not allowed')) {
      category = this.errorCategories.PERMISSION;
      userMessage = 'Microphone permission is required for voice input.';
      isRetryable = false;
      suggestions = [
        'Allow microphone access when prompted',
        'Check browser settings if permission was denied'
      ];
    }
    // Network errors
    else if (message.includes('network') || message.includes('connection')) {
      category = this.errorCategories.NETWORK;
      userMessage = 'Network connection issue. Please check your internet connection.';
      suggestions = ['Check your internet connection', 'Try again in a moment'];
    }
    // Hardware errors
    else if (message.includes('microphone') || message.includes('audio') || message.includes('device')) {
      category = this.errorCategories.HARDWARE;
      userMessage = 'Microphone issue detected. Please check your audio device.';
      suggestions = [
        'Check microphone connection',
        'Make sure microphone is not muted',
        'Try using a different microphone'
      ];
    }
    // Browser compatibility
    else if (message.includes('not supported') || message.includes('unavailable')) {
      category = this.errorCategories.BROWSER;
      userMessage = 'Voice input is not supported in your current browser.';
      isRetryable = false;
      suggestions = [
        'Try using Chrome, Edge, or Safari',
        'Update your browser to the latest version'
      ];
    }
    // HTTPS requirement
    else if (message.includes('https') || message.includes('secure')) {
      category = this.errorCategories.BROWSER;
      userMessage = 'Voice input requires a secure connection (HTTPS).';
      isRetryable = false;
      suggestions = ['Use HTTPS to access voice features'];
    }

    return {
      type: 'GENERAL_ERROR',
      category,
      message,
      userMessage,
      isRetryable,
      suggestions,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Handle error with logging and tracking
   * @param {Error} error - Error object
   * @param {string} context - Error context
   */
  handleError(error, context = 'UNKNOWN') {
    const errorKey = `${context}-${error.name || 'Error'}`;
    const count = this.errorCounts.get(errorKey) || 0;
    this.errorCounts.set(errorKey, count + 1);

    // Log error details
    console.error(`[VoiceErrorHandler] ${context}:`, {
      error: error.message,
      stack: error.stack,
      count: count + 1,
      timestamp: new Date().toISOString()
    });

    // Additional logging for frequent errors
    if (count >= 2) {
      console.warn(`[VoiceErrorHandler] Recurring error detected: ${errorKey} (${count + 1} times)`);
    }
  }

  /**
   * Check if error should trigger retry
   * @param {Object} parsedError - Parsed error object
   * @returns {boolean} - Should retry
   */
  shouldRetry(parsedError) {
    if (!parsedError.isRetryable) {
      return false;
    }

    const errorKey = `${parsedError.type}-${parsedError.category}`;
    const count = this.errorCounts.get(errorKey) || 0;
    
    return count < this.maxRetries;
  }

  /**
   * Get user-friendly error message with suggestions
   * @param {Object} parsedError - Parsed error object
   * @returns {Object} - User message object
   */
  getUserMessage(parsedError) {
    const isRecurring = (this.errorCounts.get(`${parsedError.type}-${parsedError.category}`) || 0) > 1;
    
    let message = parsedError.userMessage;
    
    if (isRecurring) {
      message += ' This issue has occurred multiple times.';
    }

    return {
      message,
      suggestions: parsedError.suggestions,
      isRetryable: parsedError.isRetryable && this.shouldRetry(parsedError),
      category: parsedError.category
    };
  }

  /**
   * Reset error counts
   */
  resetErrorCounts() {
    this.errorCounts.clear();
    console.info('[VoiceErrorHandler] Error counts reset');
  }

  /**
   * Get error statistics
   * @returns {Object} - Error statistics
   */
  getErrorStats() {
    const stats = {};
    for (const [key, count] of this.errorCounts.entries()) {
      stats[key] = count;
    }
    return {
      errorCounts: stats,
      totalErrors: Array.from(this.errorCounts.values()).reduce((a, b) => a + b, 0),
      uniqueErrors: this.errorCounts.size
    };
  }
}

export default VoiceErrorHandler;
